import React from 'react';
import { Shield, Globe, FileText, ChevronRight, Eye } from 'lucide-react';
import { ComplianceItem, ComplianceChecklist, ExpandedItems } from '../data/complianceData';
import { getRequirementLevelColor, getComplianceStatusColor, getItemCategory } from '../utils/helpers';

const COUNTRY_LABELS: Record<string, string> = {
  BE: '🇧🇪 Belgium (BE-CyFun®2023)',
  FI: '🇫🇮 Finland (Kybermittari)',
  EL: '🇬🇷 Greece (Min. Decision 1689/2025)',
  ES: '🇪🇸 Spain (ENS RD 311/2022)',
  FR: '🇫🇷 France',
};

interface Props {
  filteredData: ComplianceItem[];
  expandedItems: ExpandedItems;
  onToggleExpand: (id: string) => void;
  selectedRequirements: Set<string>;
  onToggleRequirement: (id: string) => void;
  checklist: ComplianceChecklist;
  onStatusChange: (id: string, status: string) => void;
  onSelectItem: (item: ComplianceItem) => void;
}

const GridView: React.FC<Props> = ({
  filteredData, expandedItems, onToggleExpand,
  selectedRequirements, onToggleRequirement,
  checklist, onStatusChange, onSelectItem,
}) => (
  <div className="space-y-6">
    {filteredData.map((item) => (
      <div key={item.id} className="cs-item">
        <div className="cs-item-header" onClick={() => onToggleExpand(item.id)}>
          <div className="flex-1">
            <div className="flex items-center gap-4 mb-3">
              <input
                type="checkbox"
                checked={selectedRequirements.has(item.id)}
                onChange={(e) => { e.stopPropagation(); onToggleRequirement(item.id); }}
                onClick={(e) => e.stopPropagation()}
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-lg font-semibold text-blue-600">{item.id}</span>
              <h3 className="text-xl font-semibold text-gray-800">{item.title}</h3>
              <span className="cs-badge cs-badge-blue">{getItemCategory(item.id)}</span>
              {checklist[item.id] && (
                <span className={`cs-badge ${getComplianceStatusColor(checklist[item.id])}`}>
                  {checklist[item.id].replace('-', ' ').toUpperCase()}
                </span>
              )}
            </div>

            <div className="grid grid-cols-4 gap-5 mt-5">
              {[
                { icon: <Shield className="w-5 h-5 text-gray-500 mt-1 flex-shrink-0" />, label: 'ISO 27001', value: item.iso27001 },
                { icon: <Globe className="w-5 h-5 text-gray-500 mt-1 flex-shrink-0" />, label: 'NIST', value: item.nist },
                { icon: <FileText className="w-5 h-5 text-gray-500 mt-1 flex-shrink-0" />, label: 'ETSI', value: item.etsi },
                { icon: <FileText className="w-5 h-5 text-gray-500 mt-1 flex-shrink-0" />, label: 'CEN', value: item.cen },
              ].map(({ icon, label, value }) => (
                <div key={label} className="flex items-start gap-3">
                  {icon}
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-1.5">{label}</p>
                    <p className="text-sm text-gray-700">{value.split(',').slice(0, 2).join(', ')}...</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <ChevronRight
            className={`w-6 h-6 text-gray-400 transition-transform ${expandedItems[item.id] ? 'rotate-90' : ''}`}
          />
        </div>

        {expandedItems[item.id] && (
          <div className="cs-item-expanded">
            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-gray-800 mb-4 text-lg">International Standards</h4>
                <div className="grid md:grid-cols-2 gap-5">
                  {[
                    ['ISO 27001:2022', item.iso27001],
                    ['NIST CSF v2.0', item.nist],
                    ['ETSI EN 319 401', item.etsi],
                    ['CEN/TS 18026:2024', item.cen],
                  ].map(([title, val]) => (
                    <div key={title} className="bg-white p-5 rounded-lg border border-gray-200">
                      <h5 className="font-medium text-gray-600 mb-3">{title}</h5>
                      <p className="text-sm text-gray-700 leading-relaxed">{val}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-800 mb-4 text-lg">National Frameworks</h4>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {Object.entries(item.national).map(([country, refs]) => (
                    <div key={country} className="bg-white p-5 rounded-lg border border-gray-200">
                      <h5 className="font-medium text-gray-600 mb-3">{COUNTRY_LABELS[country]}</h5>
                      <div className="text-sm space-y-2">
                        {refs.split(',').map((ref: string, idx: number) => {
                          const trimmed = ref.trim();
                          if (trimmed.includes('BASIC:') || trimmed.includes('IMPORTANT:') || trimmed.includes('ESSENTIAL:')) {
                            const [level, ...content] = trimmed.split(':');
                            return (
                              <div key={idx} className="mb-2">
                                <span className={getRequirementLevelColor(trimmed)}>{level}</span>
                                <span className="ml-3 text-gray-700">{content.join(':').trim()}</span>
                              </div>
                            );
                          }
                          return <p key={idx} className="text-gray-700 mb-2">{trimmed}</p>;
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 flex gap-4">
              <button
                onClick={(e) => { e.stopPropagation(); onSelectItem(item); }}
                className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Eye size={18} />
                View Full Details
              </button>
              <select
                value={checklist[item.id] || 'not-started'}
                onChange={(e) => { e.stopPropagation(); onStatusChange(item.id, e.target.value); }}
                onClick={(e) => e.stopPropagation()}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="not-started">Not Started</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>
        )}
      </div>
    ))}
  </div>
);

export default GridView;
